import { isValidIBAN } from 'ibantools';
import { defineStore } from 'pinia';
import { AanvragenDomainEntitiesMuterenMutatieActie as MutatieActie } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMuterenMutatieActie';
import { type AanvragenMuterenMutatieActiesDto as MutatieActiesDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenMutatieActiesDto';
import { type AanvragenMuterenStartMutatiePolisDto as SubpolisDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatiePolisDto';
import { type AanvragenMuterenStartMutatieProductDto as ProductDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatieProductDto';
import { type AanvragenMuterenStartMutatieRekeningDto as RekeningDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatieRekeningDto';
import { type AanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto as PakketPolisDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto';
import { parseDateOnlyAsLocalTime, toDateonly } from '@tools/dates';
import { ref } from 'vue';

// de datums worden opgeslagen als string omdat de pinia store niet goed met timezones om kan gaan
interface PakketpolisState {
  originelePakketpolis: PakketPolisDto;
  mutatieActies: MutatieActiesDto;
  ingangsdatum: string;
  incassomethode: string;
  incassotermijn: string;
  hoofdvervaldatum: string;
  aantalMutaties: number;
  minimaleIngangsdatum: string;
  maximaleIngangsdatum: string;
  minimaleHoofdvervaldatum: string;
  maximaleHoofdvervaldatum: string;
  rekening?: RekeningDto | undefined;
  subpolissen: SubpolisDto[];
  nieuweProducten: ProductDto[];
  mutatieKlaargezet: boolean;
  extraOpmerkingen: string;
}

export const usePakketpolisStore = defineStore('pakketPolissenStore', () => {
  const state = ref<Map<string, PakketpolisState>>(new Map<string, PakketpolisState>());

  function addPakketpolis(pakketpolis: PakketPolisDto, mutatieActies: MutatieActiesDto) {
    const pakketpolisState = {
      originelePakketpolis: pakketpolis,
      mutatieActies,
      ingangsdatum: pakketpolis.ingangsdatum,
      incassomethode: pakketpolis.incasso.code,
      incassotermijn: pakketpolis.termijn.termijn,
      hoofdvervaldatum: pakketpolis.hoofdvervaldatum,
      aantalMutaties: 0,
      minimaleIngangsdatum: mutatieActies.ingangsdatum.datumRange.minimaleDatum,
      maximaleIngangsdatum: mutatieActies.ingangsdatum.datumRange.maximaleDatum,
      minimaleHoofdvervaldatum: mutatieActies.hoofdvervaldatum.datumRange.minimaleDatum,
      maximaleHoofdvervaldatum: mutatieActies.hoofdvervaldatum.datumRange.maximaleDatum,
      subpolissen: [],
      nieuweProducten: [],
      mutatieKlaargezet: false,
      extraOpmerkingen: '',
    };

    if (!state.value.has(pakketpolis.polisnummer)) {
      state.value.set(pakketpolis.polisnummer, pakketpolisState);
    }
  }

  function setIngangsdatum(pakketpolisnummer: string, ingangsdatum: Date) {
    setPakketpolisIngangsdatum(pakketpolisnummer, ingangsdatum);
    setIngangsdatumVoorSubpolissen(pakketpolisnummer, ingangsdatum);
  }

  function setExtraOpmerkingen(pakketpolisnummer: string, opmerking: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }
    pakketpolisState.extraOpmerkingen = opmerking;
  }

  function setPakketpolisIngangsdatum(pakketpolisnummer: string, ingangsdatum: Date) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState || !pakketpolisState.mutatieActies.ingangsdatum.isMogelijk) {
      return;
    }

    const origineleDatum = pakketpolisState.originelePakketpolis.ingangsdatum;
    const huidigeDatum = pakketpolisState.ingangsdatum;
    const nieuweDatum = toDateonly(ingangsdatum);
    const isMomenteelGemuteerd = huidigeDatum !== origineleDatum;
    const isMutatie = nieuweDatum !== huidigeDatum;
    const isOrigineel = nieuweDatum === origineleDatum;

    if (!isMomenteelGemuteerd && isMutatie) {
      pakketpolisState.aantalMutaties++;
    } else if (isMomenteelGemuteerd && isOrigineel) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.ingangsdatum = nieuweDatum;
  }

  function setIngangsdatumVoorSubpolissen(pakketpolisnummer: string, ingangsdatum: Date) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState || !pakketpolisState.mutatieActies.ingangsdatum.isMogelijk) {
      return;
    }

    const subpolissen = pakketpolisState.originelePakketpolis.subpolissen.filter(
      (p) => !p.isGeroyeerd,
    );
    subpolissen.forEach((subpolis) => {
      setSubpolisIngangsdatumGemuteerd(
        pakketpolisState.originelePakketpolis.polisnummer,
        subpolis.polisnummer,
        ingangsdatum,
      );
    });
  }

  function setIncassomethode(polisnummer: string, nieuweIncassomethode: string) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState || !pakketpolisState.mutatieActies.incasso.methode.isMogelijk) {
      return;
    }

    const origineleIncassomethode = pakketpolisState.originelePakketpolis.incasso.code;
    const huidigeIncassomethode = pakketpolisState.incassomethode;

    const isMomenteelGemuteerd = huidigeIncassomethode !== origineleIncassomethode;
    const isMutatie = nieuweIncassomethode !== huidigeIncassomethode;
    const isOrigineel = nieuweIncassomethode === origineleIncassomethode;

    if (!isMomenteelGemuteerd && isMutatie) {
      pakketpolisState.aantalMutaties++;
    } else if (isMomenteelGemuteerd && isOrigineel) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.incassomethode = nieuweIncassomethode;
  }

  function setRekening(polisnummer: string, rekening: RekeningDto) {
    const pakketpolisState = state.value.get(polisnummer);
    if (
      !pakketpolisState ||
      !pakketpolisState.mutatieActies.incasso.methode.isMogelijk ||
      !isValidIBAN(rekening.iban)
    ) {
      return;
    }

    pakketpolisState.rekening = rekening;
  }

  function setIncassotermijn(polisnummer: string, nieuweIncassotermijn: string) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState || !pakketpolisState.mutatieActies.incasso.termijn.isMogelijk) {
      return;
    }

    const origineleIncassotermijn = pakketpolisState.originelePakketpolis.termijn.termijn;
    const huidigeIncassotermijn = pakketpolisState.incassotermijn;

    const isMomenteelGemuteerd = huidigeIncassotermijn !== origineleIncassotermijn;
    const isMutatie = nieuweIncassotermijn !== huidigeIncassotermijn;
    const isOrigineel = nieuweIncassotermijn === origineleIncassotermijn;

    if (!isMomenteelGemuteerd && isMutatie) {
      pakketpolisState.aantalMutaties++;
    } else if (isMomenteelGemuteerd && isOrigineel) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.incassotermijn = nieuweIncassotermijn;
  }

  function setHoofdvervaldatum(polisnummer: string, hoofdvervaldatum: Date) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState || !pakketpolisState.mutatieActies.hoofdvervaldatum.isMogelijk) {
      return;
    }

    const origineleDatum = pakketpolisState.originelePakketpolis.hoofdvervaldatum;
    const huidigeDatum = pakketpolisState.hoofdvervaldatum;
    const nieuweDatum = toDateonly(hoofdvervaldatum);
    const isMomenteelGemuteerd = huidigeDatum !== origineleDatum;
    const isMutatie = nieuweDatum !== huidigeDatum;
    const isOrigineel = nieuweDatum === origineleDatum;

    if (!isMomenteelGemuteerd && isMutatie) {
      pakketpolisState.aantalMutaties++;
    } else if (isMomenteelGemuteerd && isOrigineel) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.hoofdvervaldatum = nieuweDatum;
  }

  function clearIngangsdatum(pakketpolisnummer: string) {
    clearPakketpolisIngangsdatum(pakketpolisnummer);
    clearSubpolissenIngangsdatum(pakketpolisnummer);
  }

  function clearPakketpolisIngangsdatum(pakketpolisnummer: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    const originalDate = pakketpolisState.originelePakketpolis.ingangsdatum;
    const currentDate = pakketpolisState.ingangsdatum;

    if (originalDate !== currentDate) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.ingangsdatum = originalDate;
  }

  function clearSubpolissenIngangsdatum(pakketpolisnummer: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    const subpolissen = pakketpolisState.originelePakketpolis.subpolissen.filter(
      (p) => !p.isGeroyeerd,
    );
    subpolissen.forEach((subpolis) => {
      annuleerMutatie(pakketpolisState.originelePakketpolis.polisnummer, subpolis.polisnummer);
    });
  }

  function clearIncasso(pakketpolisnummer: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    clearIncassomethode(pakketpolisState);
    clearIncassotermijn(pakketpolisState);
  }

  function clearIncassomethode(pakketpolisState: PakketpolisState) {
    const originalIncassomethode = pakketpolisState.originelePakketpolis.incasso.code;
    const currentIncassomethode = pakketpolisState.incassomethode;

    if (originalIncassomethode !== currentIncassomethode) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.incassomethode = originalIncassomethode;
    pakketpolisState.rekening = undefined;
  }

  function clearIncassotermijn(pakketpolisState: PakketpolisState) {
    const originalIncassotermijn = pakketpolisState.originelePakketpolis.termijn.termijn;
    const currentIncassotermijn = pakketpolisState.incassotermijn;

    if (originalIncassotermijn !== currentIncassotermijn) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.incassotermijn = originalIncassotermijn;
  }

  function clearHoofdvervaldatum(polisnummer: string) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState) {
      return;
    }

    const originalDate = pakketpolisState.originelePakketpolis.hoofdvervaldatum;
    const currentDate = pakketpolisState.hoofdvervaldatum;

    if (originalDate !== currentDate) {
      pakketpolisState.aantalMutaties--;
    }

    pakketpolisState.hoofdvervaldatum = originalDate;
  }

  function getPakketpolis(polisnummer: string) {
    const pakketpolis = state.value.get(polisnummer);
    if (!pakketpolis) return undefined;

    // de datums worden omgezet naar de datum om middernacht in de huidige tijdzone
    return {
      ...pakketpolis,
      ingangsdatum: new Date(`${pakketpolis.ingangsdatum}T00:00:00.000`),
      hoofdvervaldatum: new Date(`${pakketpolis.hoofdvervaldatum}T00:00:00.000`),
      minimaleIngangsdatum: new Date(`${pakketpolis.minimaleIngangsdatum}T00:00:00.000`),
      maximaleIngangsdatum: new Date(`${pakketpolis.maximaleIngangsdatum}T00:00:00.000`),
      minimaleHoofdvervaldatum: new Date(`${pakketpolis.minimaleHoofdvervaldatum}T00:00:00.000`),
      maximaleHoofdvervaldatum: new Date(`${pakketpolis.maximaleHoofdvervaldatum}T00:00:00.000`),
    };
  }

  function setPakketpolisGeroyeerd(
    polisnummer: string,
    royeerDatum: Date,
    reden: string,
    aanvullendeReden?: string,
  ) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState) {
      return;
    }

    const royeerDatumAsDateOnly = toDateonly(royeerDatum);

    pakketpolisState.originelePakketpolis.subpolissen
      .filter((sp) => sp.isGeroyeerd === false)
      .forEach((origineleSubpolis) => {
        const subpolis = pakketpolisState.subpolissen.find(
          (x) => x.polisnummer === origineleSubpolis.polisnummer,
        );
        if (subpolis !== undefined) {
          if (subpolis.actie !== MutatieActie.Royeren) {
            subpolis.actie = MutatieActie.Royeren;
            subpolis.mutatiedatum = royeerDatumAsDateOnly;
            subpolis.mutatiereden = reden;
            subpolis.aanvullendeReden = aanvullendeReden;
          } else {
            const subRoyeerdatum = parseDateOnlyAsLocalTime(subpolis.mutatiedatum);
            if (!!subRoyeerdatum && subRoyeerdatum > royeerDatum) {
              subpolis.mutatiedatum = royeerDatumAsDateOnly;
            }
          }
          return;
        }

        pakketpolisState.subpolissen.push({
          actie: MutatieActie.Royeren,
          polisnummer: origineleSubpolis.polisnummer,
          mutatiereden: reden,
          aanvullendeReden,
          mutatiedatum: royeerDatumAsDateOnly,
        });
      });

    clearPakketpolisIngangsdatum(polisnummer);
    clearHoofdvervaldatum(polisnummer);

    pakketpolisState.aantalMutaties = pakketpolisState.subpolissen.length;
  }

  function setSubpolisGemuteerd(
    pakketpolisnummer: string,
    polisnummer: string,
    mutatiedatum: Date,
    reden: string,
  ) {
    setSubpolisMutatieActie(
      pakketpolisnummer,
      polisnummer,
      MutatieActie.Muteren,
      mutatiedatum,
      reden,
    );
  }

  function setSubpolisGeroyeerd(
    pakketpolisnummer: string,
    polisnummer: string,
    royeerdatum: Date,
    reden: string,
    redenRoyeerdatum?: string | undefined,
  ) {
    setSubpolisMutatieActie(
      pakketpolisnummer,
      polisnummer,
      MutatieActie.Royeren,
      royeerdatum,
      reden,
      redenRoyeerdatum,
    );

    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    const alleActieveSubsRoyeren = pakketpolisState.originelePakketpolis.subpolissen
      .filter((sp) => !sp.isGeroyeerd)
      .every((sp) => {
        const subpolis = pakketpolisState.subpolissen.find((x) => x.polisnummer === sp.polisnummer);
        return subpolis !== undefined && subpolis.actie === MutatieActie.Royeren;
      });

    if (alleActieveSubsRoyeren) {
      const laatstTeRoyerenSub = _getLaatstTeRoyerenSub(pakketpolisState)!;
      const laatsteRoyeerDatum = parseDateOnlyAsLocalTime(laatstTeRoyerenSub.mutatiedatum)!;

      setPakketpolisGeroyeerd(
        pakketpolisnummer,
        laatsteRoyeerDatum,
        laatstTeRoyerenSub.mutatiereden!,
        laatstTeRoyerenSub.aanvullendeReden ?? undefined,
      );
    }
  }

  function addProduct(pakketpolisnummer: string, productId: string, amount: number) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }
    for (let i = 0; i < amount; i++) {
      pakketpolisState.nieuweProducten.push({ productId, productSoort: 'Volmacht' });
      pakketpolisState.aantalMutaties++;
    }
  }

  function removeProduct(pakketpolisnummer: string, productId: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }
    const idx = pakketpolisState.nieuweProducten.findIndex((x) => x.productId === productId);
    pakketpolisState.nieuweProducten.splice(idx, 1);
    pakketpolisState.aantalMutaties--;
  }

  function setSubpolisIngangsdatumGemuteerd(
    pakketpolisnummer: string,
    polisnummer: string,
    ingangsdatum: Date,
  ) {
    setSubpolisMutatieActie(
      pakketpolisnummer,
      polisnummer,
      MutatieActie.MuterenIngangsdatum,
      ingangsdatum,
      'Mutatie ingangsdatum',
    );
  }

  function setSubpolisMutatieActie(
    pakketpolisnummer: string,
    polisnummer: string,
    mutatieActie: MutatieActie,
    mutatiedatum: Date,
    reden: string,
    aanvullendeReden?: string,
  ) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    const polis = pakketpolisState.subpolissen.find((x) => x.polisnummer === polisnummer);

    if (
      isIngangsdatumMutatieMetOrigineleIngangsdatum(
        pakketpolisState,
        polisnummer,
        mutatieActie,
        mutatiedatum,
      )
    ) {
      if (polis?.actie === MutatieActie.MuterenIngangsdatum) {
        annuleerMutatie(pakketpolisnummer, polisnummer);
      }
      return;
    }

    if (!polis) {
      pakketpolisState.subpolissen.push({
        polisnummer,
        actie: mutatieActie,
        mutatiereden: reden,
        mutatiedatum: toDateonly(mutatiedatum),
        aanvullendeReden,
      });
      pakketpolisState.aantalMutaties++;
      return;
    }

    if (!polis.actie) {
      pakketpolisState.aantalMutaties++;
    } else if (
      polis.actie === MutatieActie.MuterenIngangsdatum &&
      mutatieActie === MutatieActie.Muteren
    ) {
      // als een subpolis ingangsdatum mutatie overschreven wordt, annuleer de pakketpolis ingangsdatum mutatie
      clearPakketpolisIngangsdatum(pakketpolisnummer);
    }

    polis.actie = mutatieActie;
    polis.mutatiedatum = toDateonly(mutatiedatum);
    polis.mutatiereden = reden;
  }

  function isIngangsdatumMutatieMetOrigineleIngangsdatum(
    pakketpolisState: PakketpolisState,
    polisnummer: string,
    mutatieActie: MutatieActie,
    mutatiedatum: Date,
  ) {
    if (mutatieActie !== MutatieActie.MuterenIngangsdatum) {
      return false;
    }

    const originelePolis = pakketpolisState.originelePakketpolis.subpolissen.find(
      (p) => p.polisnummer === polisnummer,
    );
    const origineleIngangsdatum = parseDateOnlyAsLocalTime(originelePolis?.ingangsdatum);
    return originelePolis && origineleIngangsdatum?.getTime() === mutatiedatum.getTime();
  }

  function annuleerMutatie(pakketpolisnummer: string, polisnummerSub?: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    if (polisnummerSub) {
      const index = pakketpolisState.subpolissen.findIndex(
        (s) => s?.polisnummer === polisnummerSub,
      );
      if (index < 0) {
        return;
      }

      pakketpolisState.subpolissen.splice(index, 1);
      pakketpolisState.aantalMutaties--;
      clearPakketpolisIngangsdatum(pakketpolisnummer);

      return;
    }

    // Indien geen polisnummerSub meegegeven is, mutaties op de pakketpolis resetten.
    clearPakketpolisIngangsdatum(pakketpolisnummer);
    clearHoofdvervaldatum(pakketpolisnummer);

    // Indien pakket geroyeerd wordt, laatste royementsdatum pakken en alle polissen die op die datum geroyeerd worden resetten.
    // Dit is nodig omdat het mogelijk is enkele polissen te royeren vóór het hele pakket geroyeerd wordt,
    // bij annuleren van het pakket royement moeten die intact blijven.
    if (getPakketGeroyeerd(pakketpolisnummer)) {
      const laatstTeRoyerenSub = _getLaatstTeRoyerenSub(pakketpolisState)!;
      for (let i = pakketpolisState.subpolissen.length - 1; i >= 0; i--) {
        const subpolis = pakketpolisState.subpolissen[i];
        if (
          subpolis.actie === MutatieActie.Royeren &&
          subpolis.mutatiedatum === laatstTeRoyerenSub.mutatiedatum
        ) {
          pakketpolisState.subpolissen.splice(i, 1);
        }
      }
      pakketpolisState.aantalMutaties = pakketpolisState.subpolissen.length;
    }
  }

  function getPakketGeroyeerd(pakketpolisnummer: string) {
    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return;
    }

    const alleActiesZijnRoyementen =
      pakketpolisState.subpolissen.length > 0 &&
      pakketpolisState.subpolissen.every((sp) => sp.actie === MutatieActie.Royeren);

    const alleActieveSubpolissenZijnAangepast =
      pakketpolisState.originelePakketpolis.subpolissen.filter((sp) => !sp.isGeroyeerd).length ===
      pakketpolisState.subpolissen.length;

    return alleActiesZijnRoyementen && alleActieveSubpolissenZijnAangepast;
  }

  function heeftAlleenRoyeerMutaties(pakketpolisnummer: string) {
    if (getPakketGeroyeerd(pakketpolisnummer)) {
      return true;
    }

    const pakketpolisState = state.value.get(pakketpolisnummer);
    if (!pakketpolisState) {
      return false;
    }

    return (
      pakketpolisState.subpolissen.filter((s) => s.actie === MutatieActie.Royeren).length ===
      pakketpolisState.aantalMutaties
    );
  }

  function setMutatieKlaargezet(polisnummer: string) {
    const pakketpolisState = state.value.get(polisnummer);
    if (!pakketpolisState) {
      return;
    }

    pakketpolisState.mutatieKlaargezet = true;
  }

  function clear() {
    return state.value.clear();
  }

  function _getLaatstTeRoyerenSub(pakketpolis: PakketpolisState): SubpolisDto | undefined {
    return pakketpolis.subpolissen.reduce(
      (laatsteSub, subpolis) => {
        if (laatsteSub === undefined) return subpolis;

        return laatsteSub.mutatiedatum! > subpolis.mutatiedatum! ? laatsteSub : subpolis;
      },
      undefined as SubpolisDto | undefined,
    )!;
  }

  return {
    getPakketpolis,
    clear,
    addPakketpolis,
    setIngangsdatum,
    clearIngangsdatum,
    setIncassomethode,
    setRekening,
    setIncassotermijn,
    clearIncasso,
    setHoofdvervaldatum,
    clearHoofdvervaldatum,
    setPakketpolisGeroyeerd,
    annuleerMutatie,
    getPakketGeroyeerd,
    setSubpolisGemuteerd,
    setSubpolisGeroyeerd,
    setMutatieKlaargezet,
    setSubpolisIngangsdatumGemuteerd,
    addProduct,
    removeProduct,
    setExtraOpmerkingen,
    heeftAlleenRoyeerMutaties,
  };
});
